import React, { useEffect, useState } from "react";
import "../HomePage/HomePage.css";
import ApiName from "../../../ApiName";
import axios from "axios";
import Carousel from "../../../Dashboard_en/DashboardBreadcrumb/Carousel";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";

const HomePage = () => {
  // Fetch home images
  const { data: images = [], isLoading: isLoadingImages } = useQuery({
    queryKey: ["homeImages"],
    queryFn: async () => {
      const response = await axios.get(`${ApiName}/homeImage`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch home settings (quote)
  const { data: homeData = { quoteText: "", quoteAuthor: "" }, isLoading: isLoadingData } = useQuery({
    queryKey: ["homeSettings"],
    queryFn: async () => {
      const response = await axios.get(`${ApiName}/Home`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = isLoadingImages || isLoadingData;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 min-h-[80vh] flex flex-col justify-center"
    >
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div 
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 gap-12 lg:grid-cols-2 animate-pulse"
          >
            <div className="flex flex-col justify-center space-y-6">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-11/12"></div>
              <div className="h-4 bg-gray-200 rounded w-9/12"></div>
              <div className="h-3 bg-gray-200 rounded w-1/3 mt-8"></div>
              <div className="hidden lg:block h-12 bg-gray-200 rounded w-40 mt-12"></div>
            </div>
            <div className="w-full aspect-[4/3] bg-gray-200 rounded-sm"></div>
          </motion.div>
        ) : (
          <motion.div 
            key="content"
            className="grid grid-cols-1 gap-16 lg:grid-cols-2 items-center"
          >
            {/* Left Column: Typography / Quote */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="flex flex-col justify-center pr-0 lg:pr-8"
            >
              <div className="relative">
                {/* Decorative Quote Mark */}
                <span className="absolute -top-10 -left-6 text-8xl text-gray-100 font-serif opacity-50 select-none">
                  "
                </span>
                
                <p className="relative z-10 text-xl md:text-2xl font-serif italic text-justify leading-relaxed text-gray-800 whitespace-pre-wrap">
                  {homeData.quoteText}
                </p>
                
                <div className="mt-8 flex items-center gap-4">
                  <div className="w-12 h-[1px] bg-gray-400"></div>
                  <p className="text-sm md:text-base tracking-widest uppercase sans-bold text-gray-500">
                    {homeData.quoteAuthor}
                  </p>
                </div>
              </div>

              {/* Call to Action (Desktop) */}
              <div className="hidden lg:block mt-16">
                <Link to="/main/shop">
                  <motion.button 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="group relative overflow-hidden px-10 py-4 bg-zinc-900 text-white text-sm tracking-[0.2em] uppercase transition-all duration-300"
                  >
                    <span className="relative z-10">Browse Portfolio</span>
                    <div className="absolute inset-0 bg-zinc-800 scale-x-0 origin-left transition-transform duration-300 ease-out group-hover:scale-x-100"></div>
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            {/* Right Column: Carousel */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="w-full relative shadow-2xl rounded-sm overflow-hidden bg-white"
            >
              {images.length > 0 ? (
                <div className="aspect-[4/3] w-full">
                  <Carousel images={images} />
                </div>
              ) : (
                <div className="aspect-[4/3] w-full bg-gray-100 flex items-center justify-center text-gray-400 sans-light">
                  No images available
                </div>
              )}
            </motion.div>

            {/* Call to Action (Mobile) */}
            <div className="lg:hidden mt-4 flex justify-center">
              <Link to="/main/shop" className="w-full">
                <button className="w-full px-8 py-4 bg-zinc-900 text-white text-sm tracking-[0.2em] uppercase active:bg-zinc-800 transition-colors">
                  Browse Portfolio
                </button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default HomePage;
